import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { authenticate, logout as authLogout, getCurrentUser } from './auth/authentication';
import LanguageProvider from './contexts/LanguageContext';

// Components
import { Toaster } from 'sonner';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login from './components/Login';
import Home from './components/Home';
import Dashboard from './components/Dashboard';
// import ForestAtlas from './components/ForestAtlas';
import CaseManagement from './components/CaseManagement';
import Analytics from './components/Analytics';
import AdminPanel from './components/AdminPanel';
import FRAAtlasAdmin from './components/FRAAtlasAdmin';
import CSVDataUpload from './components/CSVDataUpload';
import OfficerDashboard from './components/OfficerDashboard';
import PublicTransparencyPortal from './components/PublicTransparencyPortal';
import CitizenPortal from './components/CitizenPortal';
import SimpleMapTest from './components/SimpleMapTest';
import ForestAtlasFixed from './components/ForestAtlasFixed';
import ForestAtlasGoogleMaps from './components/ForestAtlasGoogleMaps';
import ForestMonitoringDashboard from './components/ForestMonitoringDashboard';

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getCurrentUser());
  const [loading, setLoading] = useState(false);

  const login = async (username, password) => {
    setLoading(true);
    try {
      const result = authenticate(username, password);
      if (result.success) {
        setUser(result.user);
        return { success: true };
      }
      return { success: false, error: result.message };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: 'Login failed' };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authLogout();
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto"></div>
          <p className="mt-2 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Main Layout Component
const MainLayout = ({ children }) => {
  // Set sidebar closed on mobile by default, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50">
      <Header 
        user={user} 
        sidebarOpen={sidebarOpen} 
        setSidebarOpen={setSidebarOpen} 
      />
      <div className="flex pt-[120px]">
        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        <Sidebar 
          isOpen={sidebarOpen} 
          userRole={user?.role}
          onNavigate={() => {
            // Close sidebar on mobile after navigation
            if (window.innerWidth < 1024) {
              setSidebarOpen(false);
            }
          }}
        />
        <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'} min-h-[calc(100vh-120px)] w-full`}>
          <div className="p-4 sm:p-6 h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public Routes - No login required */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/atlas" element={<ForestAtlasGoogleMaps />} />
              <Route path="/citizen-portal" element={<CitizenPortal />} />
              
              {/* Protected Routes - Login required */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <MainLayout>
                    <Dashboard />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/monitoring" element={
                <ProtectedRoute>
                  <ForestMonitoringDashboard />
                </ProtectedRoute>
              } />
              <Route path="/atlas-old" element={
                <ProtectedRoute>
                  <MainLayout>
                    <ForestAtlasFixed />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/map-test" element={
                <ProtectedRoute>
                  <MainLayout>
                    <SimpleMapTest />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/cases" element={
                <ProtectedRoute>
                  <MainLayout>
                    <CaseManagement />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/analytics" element={
                <ProtectedRoute>
                  <MainLayout>
                    <Analytics />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <MainLayout>
                    <AdminPanel />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/fra-admin" element={
                <ProtectedRoute>
                  <MainLayout>
                    <FRAAtlasAdmin />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/data-upload" element={
                <ProtectedRoute>
                  <MainLayout>
                    <CSVDataUpload />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/officer-dashboard" element={
                <ProtectedRoute>
                  <MainLayout>
                    <OfficerDashboard />
                  </MainLayout>
                </ProtectedRoute>
              } />
              <Route path="/transparency" element={
                <PublicTransparencyPortal />
              } />
              <Route path="/citizen-portal-old" element={
                <MainLayout>
                  <CitizenPortal />
                </MainLayout>
              } />
            </Routes>
            <Toaster position="top-right" richColors />
          </div>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;